import React from "react";
import { Link } from "react-router-dom";
import RealImage from "@/components/RealImage";
import { Seo } from "@/components/Seo";
import { TEAM } from "@/lib/data";
import { ArrowRight } from "lucide-react";
import { useT } from "@/lib/i18n";

const TEAM_PHOTOS = { pietro: "/images/team/pietro.jpg", benjamin: "/images/team/benjamin.jpg", nata: "/images/team/nata.jpg", lucas: "/images/team/lucas.jpg" };

const Sec = ({ children, className = "", id }) => (
  <section id={id} className={`py-20 md:py-28 relative ${className}`}>
    <div className="container-ar relative z-10">{children}</div>
  </section>
);

export default function About() {
  const t = useT();
  const roleByTeam = { pietro: t.role_pm, benjamin: t.role_md, nata: t.role_cad, lucas: t.role_mfg };
  const bioByTeam = { pietro: t.bio_pietro, benjamin: t.bio_benjamin, nata: t.bio_nata, lucas: t.bio_lucas };
  const values = [t.val_1, t.val_2, t.val_3, t.val_4, t.val_5, t.val_6, t.val_7, t.val_8];
  const journey = [t.j_1, t.j_2, t.j_3, t.j_4, t.j_5, t.j_6];
  const diffs = [t.d_1, t.d_2, t.d_3, t.d_4, t.d_5, t.d_6, t.d_7];
  return (
    <>
      <Seo title={t.seo_about_title} description={t.seo_about_desc} />
      <section className="relative pt-16 pb-16 carbon-tex" data-testid="about-hero">
        <div className="speed-lines" />
        <div className="container-ar relative z-10">
          <div className="eyebrow mb-4">{t.nav_about}</div>
          <h1 className="h1-hero text-5xl md:text-7xl mb-6">{t.about_h1_a} <span className="text-[#e77300]">{t.about_h1_b}</span></h1>
          <p className="text-white/80 text-lg max-w-3xl">{t.about_intro}</p>
        </div>
      </section>

      <Sec>
        <div className="grid lg:grid-cols-2 gap-10">
          <div className="tele-card p-8" data-testid="mission-card">
            <div className="eyebrow mb-3">{t.lb_mission}</div>
            <p className="font-display text-2xl md:text-3xl uppercase leading-tight">{t.mission_copy}</p>
          </div>
          <div className="tele-card p-8" data-testid="vision-card">
            <div className="eyebrow mb-3">{t.lb_vision}</div>
            <p className="font-display text-2xl md:text-3xl uppercase leading-tight text-[#e77300]">{t.vision_copy}</p>
          </div>
        </div>
      </Sec>

      <Sec className="bg-[#022a67]/10 border-y border-[#022a67]">
        <div className="eyebrow mb-3">{t.lb_values}</div>
        <h2 className="h2-section text-4xl md:text-5xl mb-10">{t.h_stand}</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {values.map((v, i) => (
            <div key={i} className="tele-card p-6 text-center" data-testid={`value-${i}`}>
              <div className="font-mono-alt text-[10px] text-[#005fca]">{String(i + 1).padStart(2, "0")}</div>
              <div className="font-display text-xl uppercase mt-2">{v}</div>
            </div>
          ))}
        </div>
      </Sec>

      <Sec id="team">
        <div className="eyebrow mb-3">{t.lb_ourteam}</div>
        <h2 className="h2-section text-4xl md:text-5xl mb-10">{t.h_team}</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {TEAM.map((m) => (
            <div key={m.id} className="tele-card overflow-hidden" data-testid={`about-team-${m.id}`}>
              <RealImage src={TEAM_PHOTOS[m.id]} alt={m.name} ratio="4/5" fit="cover" frameless className="border-b border-[#005fca]/30" />
              <div className="p-5">
                <div className="flex items-center justify-between mb-2">
                  <div className="font-display text-xl uppercase">{m.name}</div>
                  <div className="font-mono-alt text-xs text-[#e77300]">#{m.number}</div>
                </div>
                <div className="eyebrow">{roleByTeam[m.id]}</div>
                <p className="text-sm text-[#8ba3cc] mt-3">{bioByTeam[m.id]}</p>
                <a href="#" className="text-xs font-mono-alt tracking-widest text-[#005fca] hover:text-[#e77300] mt-4 inline-block uppercase">LinkedIn →</a>
              </div>
            </div>
          ))}
        </div>
      </Sec>

      <Sec className="border-y border-[#022a67] bg-[#022a67]/10">
        <div className="eyebrow mb-3">{t.lb_journey}</div>
        <h2 className="h2-section text-4xl md:text-5xl mb-10">{t.h_journey}</h2>
        <div className="grid md:grid-cols-6 gap-4">
          {journey.map((step, i) => (
            <div key={i} className="tele-card p-5" data-testid={`journey-${i}`}>
              <div className="font-mono-alt text-[10px] text-[#e77300] mb-2">{t.step} {String(i + 1).padStart(2, "0")}</div>
              <div className={`font-display uppercase text-sm ${i === 4 ? "text-[#fdb429]" : ""}`}>{step}</div>
            </div>
          ))}
        </div>
      </Sec>

      <Sec>
        <div className="eyebrow mb-3">{t.lb_different}</div>
        <h2 className="h2-section text-4xl md:text-5xl mb-10">{t.h_ppp}</h2>
        <div className="grid md:grid-cols-3 gap-4">
          {diffs.map((r, i) => (
            <div key={i} className="tele-card p-6" data-testid={`different-${i}`}>
              <div className="font-mono-alt text-[10px] text-[#005fca] mb-2">{String(i + 1).padStart(2, "0")}</div>
              <div className="font-display uppercase text-lg">{r}</div>
            </div>
          ))}
        </div>
        <div className="mt-12 flex justify-center">
          <Link to="/sponsorship" className="btn-racing primary" data-testid="about-become-sponsor"><span>{t.nav_become_sponsor} <ArrowRight className="w-4 h-4" /></span></Link>
        </div>
      </Sec>
    </>
  );
}
